import { usePromotions } from "./PromotionContext";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axiosInstance from "../api/axiosInstance";
import { useCustomer } from "./CustomerContext";
import {
  enrichWishlistAvailability,
  getWishlistProductId,
  normalizeWishlistPayload,
} from "../utils/wishlistProducts";

const WishlistContext = createContext();

const GUEST_WISHLIST_KEY = "guestWishlist";
const LEGACY_WISHLIST_KEY = "wishlist";

const getProductId = getWishlistProductId;

const loadProductAvailability = async (productCode) => {
  const response = await axiosInstance.get("/stock-batches/public", {
    params: { productCode },
  });
  return response.data?.totalAvailable;
};

const normalizeUserWishlist = (value) =>
  enrichWishlistAvailability(value, loadProductAvailability);

const readStoredGuestWishlist = () => {
  try {
    return normalizeWishlistPayload(
      JSON.parse(localStorage.getItem(GUEST_WISHLIST_KEY)),
    );
  } catch {
    return [];
  }
};

const writeStoredGuestWishlist = (wishlist) => {
  localStorage.setItem(GUEST_WISHLIST_KEY, JSON.stringify(wishlist));
};

export const useWishlist = () => useContext(WishlistContext);

export const WishlistProvider = ({ children }) => {
  const { customer } = useCustomer();
  const { revision: promotionRevision } = usePromotions();
  const customerId = customer?._id || customer?.id || null;
  const isAuthenticated = Boolean(customerId);
  const [guestWishlist, setGuestWishlist] = useState(() => {
    const storedGuestWishlist = readStoredGuestWishlist();

    // The old key mixed guest and authorized-user data. Do not migrate it.
    localStorage.removeItem(LEGACY_WISHLIST_KEY);

    return storedGuestWishlist;
  });
  const [userWishlist, setUserWishlist] = useState([]);
  const [isWishlistLoading, setIsWishlistLoading] = useState(false);

  useEffect(() => {
    writeStoredGuestWishlist(guestWishlist);
  }, [guestWishlist]);

  useEffect(() => {
    if (isAuthenticated || !guestWishlist.length) return undefined;

    const productCodes = [
      ...new Set(
        guestWishlist
          .map((product) => product?.productCode)
          .filter((productCode) => productCode != null && productCode !== "")
          .map(String),
      ),
    ];
    if (!productCodes.length) return undefined;

    let isCancelled = false;
    axiosInstance
      .get("/products/by-codes", { params: { codes: productCodes.join(",") } })
      .then(({ data }) => {
        if (isCancelled) return;
        const visibleCodes = new Set(
          (Array.isArray(data) ? data : [])
            .map((product) => String(product?.productCode || ""))
            .filter(Boolean),
        );
        setGuestWishlist((current) => {
          const byCode = new Map((Array.isArray(data) ? data : []).map((p) => [String(p.productCode), p]));
          const next = current.filter((product) => {
            const code = product?.productCode;
            return code == null || code === "" || visibleCodes.has(String(code));
          });
          const updated = next.map((p) => { const latest = byCode.get(String(p.productCode)); return latest && (latest.price !== p.price || latest.discountPrice !== p.discountPrice) ? { ...p, ...latest } : p; });
          return updated.length === current.length && updated.every((p, i) => p === current[i]) ? current : updated;
        });
      })
      .catch((error) => {
        console.error("Failed to refresh guest wishlist visibility:", error);
      });

    return () => {
      isCancelled = true;
    };
  }, [guestWishlist, isAuthenticated, promotionRevision]);

  useEffect(() => {
    let isCancelled = false;

    const fetchUserWishlist = async () => {
      if (!customerId) {
        setUserWishlist([]);
        setIsWishlistLoading(false);
        return;
      }

      setUserWishlist([]);
      setIsWishlistLoading(true);

      try {
        const response = await axiosInstance.get("/customers/me/wishlist");
        const normalizedWishlist = await normalizeUserWishlist(response.data);
        if (!isCancelled) {
          setUserWishlist(normalizedWishlist);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error("Failed to load customer wishlist:", error);
          setUserWishlist([]);
        }
      } finally {
        if (!isCancelled) {
          setIsWishlistLoading(false);
        }
      }
    };

    fetchUserWishlist();

    return () => {
      isCancelled = true;
    };
  }, [customerId, promotionRevision]);

  const activeWishlist = isAuthenticated ? userWishlist : guestWishlist;

  const addToWishlist = useCallback(
    async (product) => {
      const productId = getProductId(product);
      if (!productId) return;

      if (!isAuthenticated) {
        setGuestWishlist((prev) =>
          prev.some((item) => getProductId(item) === productId)
            ? prev
            : [...prev, product],
        );
        return;
      }

      setUserWishlist((prev) =>
        prev.some((item) => getProductId(item) === productId)
          ? prev
          : [...prev, product],
      );

      try {
        const response = await axiosInstance.post("/customers/me/wishlist", {
          productId,
        });
        setUserWishlist(await normalizeUserWishlist(response.data));
      } catch (error) {
        console.error("Failed to add product to customer wishlist:", error);
        setUserWishlist((prev) =>
          prev.filter((item) => getProductId(item) !== productId),
        );
      }
    },
    [isAuthenticated],
  );

  const removeFromWishlist = useCallback(
    async (productOrId) => {
      const productId = getProductId(productOrId);
      if (!productId) return;

      if (!isAuthenticated) {
        setGuestWishlist((prev) =>
          prev.filter((item) => getProductId(item) !== productId),
        );
        return;
      }

      const previousWishlist = userWishlist;
      setUserWishlist((prev) =>
        prev.filter((item) => getProductId(item) !== productId),
      );

      try {
        const response = await axiosInstance.delete(
          `/customers/me/wishlist/${encodeURIComponent(productId)}`,
        );
        setUserWishlist(await normalizeUserWishlist(response.data));
      } catch (error) {
        console.error("Failed to remove product from customer wishlist:", error);
        setUserWishlist(previousWishlist);
      }
    },
    [isAuthenticated, userWishlist],
  );

  const clearUserWishlistState = useCallback(() => {
    setUserWishlist([]);
  }, []);

  const isInWishlist = useCallback(
    (id) => {
      const productId = getProductId(id);
      return activeWishlist.some((item) => getProductId(item) === productId);
    },
    [activeWishlist],
  );

  const value = useMemo(
    () => ({
      wishlist: activeWishlist,
      guestWishlist,
      userWishlist,
      isAuthenticated,
      isWishlistLoading,
      addToWishlist,
      removeFromWishlist,
      isInWishlist,
      clearUserWishlistState,
    }),
    [
      activeWishlist,
      guestWishlist,
      userWishlist,
      isAuthenticated,
      isWishlistLoading,
      addToWishlist,
      removeFromWishlist,
      isInWishlist,
      clearUserWishlistState,
    ],
  );

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};
